import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { Plus, Edit, Trash2, Calendar, Wallet, TrendingUp, TrendingDown, Scale, ArrowUpCircle, ArrowDownCircle, Camera, Clapperboard, Monitor, Megaphone, Building, BookOpen, Music, Wrench, Settings, Tag, DollarSign, CreditCard, CheckCircle, XCircle, AlertCircle, Lightbulb } from 'lucide-react';
import { toast } from 'sonner';

const API = `${import.meta.env.VITE_BACKEND_URL}/api`;

const CORES_DISPONIVEIS = [
  '#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#8B5CF6',
  '#EC4899', '#06B6D4', '#6366F1', '#14B8A6', '#F97316'
];

const TIPOS_CONTA = [
  { value: 'conta_corrente', label: 'Conta Corrente' },
  { value: 'poupanca', label: 'Poupança' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'investimento', label: 'Investimento' }
];

// Categorias de despesas (padrão - será substituído pelas categorias do usuário)
const CATEGORIAS_DESPESAS = [
  { value: 'equipamentos', label: 'Equipamentos', icon: Camera, color: '#3B82F6' },
  { value: 'producao', label: 'Produção', icon: Clapperboard, color: '#8B5CF6' },
  { value: 'software', label: 'Software', icon: Monitor, color: '#06B6D4' },
  { value: 'marketing', label: 'Marketing', icon: Megaphone, color: '#F59E0B' },
  { value: 'infraestrutura', label: 'Infraestrutura', icon: Building, color: '#6366F1' },
  { value: 'educacao', label: 'Educação', icon: BookOpen, color: '#10B981' },
  { value: 'licencas', label: 'Licenças', icon: Music, color: '#EC4899' },
  { value: 'outros', label: 'Outros', icon: Wrench, color: '#64748B' }
];

const TransacoesPage = () => {
  const { theme: themeColors, isDarkMode } = useTheme();
  const { isMobile } = useSidebar();
  
  // View Control
  const [currentView, setCurrentView] = useState('transacoes'); // 'transacoes', 'categorias', 'contas'
  const [showConfigMenu, setShowConfigMenu] = useState(false);
  
  // States
  const [transacoes, setTransacoes] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [contas, setContas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [tipoTransacao, setTipoTransacao] = useState(''); // 'receita' ou 'despesa'
  const [editingTransacao, setEditingTransacao] = useState(null);
  
  // Config modals
  const [showCategoriaModal, setShowCategoriaModal] = useState(false);
  const [showContaModal, setShowContaModal] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [editingConta, setEditingConta] = useState(null);
  
  const [resumo, setResumo] = useState({
    saldo_atual: 0,
    total_receitas: 0,
    total_despesas: 0,
    balanco_mensal: 0
  });
  
  // Form states
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    descricao: '',
    categoria_id: '',
    conta_id: '',
    valor: '',
    pago_recebido: true,
    parcelado: false,
    numero_parcelas: 2
  });

  // Filters
  const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
  const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
  const [filtroTipo, setFiltroTipo] = useState(''); // '', 'receita', 'despesa'
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [tempMes, setTempMes] = useState(new Date().getMonth() + 1);
  const [tempAno, setTempAno] = useState(new Date().getFullYear());

  // Fatura de Cartão
  const [cartaoSelecionado, setCartaoSelecionado] = useState(null);
  const [fatura, setFatura] = useState(null);
  const [loadingFatura, setLoadingFatura] = useState(false);
  const [showLiquidarModal, setShowLiquidarModal] = useState(false);

  // Modal de Exclusão de Compra de Cartão
  const [showDeleteCartaoModal, setShowDeleteCartaoModal] = useState(false);
  const [compraParaExcluir, setCompraParaExcluir] = useState(null);
  const [deleteAction, setDeleteAction] = useState('todas'); // 'esta' | 'futuras' | 'todas'

  // Modal de Exclusão de Despesa Normal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transacaoParaExcluir, setTransacaoParaExcluir] = useState(null);

  // Modal de Reabrir Fatura
  const [showReabrirFaturaModal, setShowReabrirFaturaModal] = useState(false);
  const [compraParaReabrir, setCompraParaReabrir] = useState(null);

  // Filtro por Conta
  const [filtroContaId, setFiltroContaId] = useState('');

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const filterMenu = document.getElementById('filter-menu');
      const dropdownMenu = document.getElementById('dropdown-menu');
      const datePickerModal = document.getElementById('date-picker-modal');
      
      // Close filter menu if clicking outside
      if (filterMenu && !filterMenu.contains(event.target)) {
        const filterButton = document.getElementById('filter-type-button');
        if (!filterButton || !filterButton.contains(event.target)) {
          filterMenu.style.display = 'none';
        }
      }
      
      // Close dropdown menu if clicking outside
      if (dropdownMenu && !dropdownMenu.contains(event.target)) {
        const dropdownButton = document.getElementById('nova-transacao-button');
        if (!dropdownButton || !dropdownButton.contains(event.target)) {
          dropdownMenu.style.display = 'none';
        }
      }
      
      // Close date picker if clicking outside
      if (datePickerModal && !datePickerModal.contains(event.target) && showDatePicker) {
        const dateButton = document.getElementById('date-filter-button');
        if (!dateButton || !dateButton.contains(event.target)) {
          setShowDatePicker(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDatePicker]);

  useEffect(() => {
    loadTransacoes();
    loadResumo();
  }, [filtroMes, filtroAno, filtroTipo, filtroContaId]);

  useEffect(() => {
    // Carregar categorias e contas ao montar o componente
    loadCategorias();
    loadContas();
  }, []);

  // Selecionar primeiro cartão automaticamente
  useEffect(() => {
    if (contas.length > 0 && !cartaoSelecionado) {
      const cartoes = contas.filter(c => c.tipo === 'cartao_credito');
      if (cartoes.length > 0) {
        setCartaoSelecionado(cartoes[0].id);
      }
    }
  }, [contas]);

  // Carregar fatura quando cartão ou mês mudar
  useEffect(() => {
    if (cartaoSelecionado) {
      loadFatura();
    }
  }, [cartaoSelecionado, filtroMes, filtroAno]);

  const loadFatura = async () => {
    if (!cartaoSelecionado) return;
    
    try {
      setLoadingFatura(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financeiro/fatura-cartao`, {
        params: {
          cartao_id: cartaoSelecionado,
          mes: filtroMes,
          ano: filtroAno
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFatura(response.data.fatura);
    } catch (error) {
      console.error('Error loading fatura:', error);
      setFatura(null);
    } finally {
      setLoadingFatura(false);
    }
  };

  const handleLiquidarFatura = async () => {
    if (!cartaoSelecionado || !fatura) return;
    
    // Buscar contas que não sejam cartão de crédito
    const contasPagamento = contas.filter(c => c.tipo !== 'cartao_credito');
    
    if (contasPagamento.length === 0) {
      toast.error('Você precisa ter uma conta corrente/poupança cadastrada para pagar a fatura');
      return;
    }
    
    // Abrir modal de pagamento
    setShowLiquidarModal(true);
  };

  const [contaPagamentoSelecionada, setContaPagamentoSelecionada] = useState('');

  const confirmarPagamentoFatura = async () => {
    if (!contaPagamentoSelecionada) {
      toast.error('Selecione uma conta para debitar o pagamento');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/financeiro/fatura-cartao/liquidar`,
        {
          cartao_id: cartaoSelecionado,
          mes: filtroMes,
          ano: filtroAno,
          conta_pagamento_id: contaPagamentoSelecionada
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Fatura paga com sucesso!');
      
      // Fechar modal
      setShowLiquidarModal(false);
      setContaPagamentoSelecionada('');
      
      // Recarregar tudo
      loadFatura();
      loadTransacoes();
      loadResumo();
    } catch (error) {
      console.error('Error liquidando fatura:', error);
      toast.error(error.response?.data?.detail || 'Erro ao liquidar fatura');
    }
  };

  // Função para reabrir fatura (estornar pagamento)
  const handleReabrirFatura = async (compra) => {
    setCompraParaReabrir(compra);
    setShowReabrirFaturaModal(true);
  };

  const confirmarReabrirFatura = async () => {
    if (!compraParaReabrir) return;

    const conta = contas.find(c => c.id === compraParaReabrir.conta_id);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/financeiro/fatura-cartao/reabrir`,
        {
          cartao_id: compraParaReabrir.conta_id,
          mes: filtroMes,
          ano: filtroAno
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message || 'Fatura reaberta! Agora você pode excluir a compra.');
      
      // Fechar modal
      setShowReabrirFaturaModal(false);
      setCompraParaReabrir(null);
      
      // Recarregar dados
      loadFatura();
      loadTransacoes();
      loadResumo();
      
      // Reabrir modal de exclusão
      setShowDeleteCartaoModal(true);
    } catch (error) {
      console.error('Error reabrindo fatura:', error);
      toast.error(error.response?.data?.detail || 'Erro ao reabrir fatura');
    }
  };

  // Função para excluir compra de cartão
  const handleExcluirCompraCartao = async () => {
    if (!compraParaExcluir) return;

    try {
      const token = localStorage.getItem('token');
      
      // Verificar se é parcela de cartão (tem compra_cartao_id) ou despesa normal
      const isParcelaCartao = compraParaExcluir.compra_cartao_id;
      
      if (!isParcelaCartao) {
        toast.error('Esta não é uma compra de cartão parcelada');
        return;
      }
      
      // Usar o ID da COMPRA, não da PARCELA
      const compraId = compraParaExcluir.compra_cartao_id;
      
      const response = await axios.delete(
        `${API}/cartoes/compras/${compraId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { action: deleteAction }
        }
      );
      
      toast.success(response.data.message || 'Compra excluída com sucesso!');
      
      // Fechar modal e recarregar
      setShowDeleteCartaoModal(false);
      setCompraParaExcluir(null);
      loadTransacoes();
      loadResumo();
      loadFatura();
    } catch (error) {
      console.error('Error excluindo compra:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao excluir compra';
      
      // Se erro diz que tem parcela paga, oferecer reabrir fatura automaticamente
      if (errorMsg.includes('PAGA') || errorMsg.includes('paga')) {
        setShowDeleteCartaoModal(false);
        handleReabrirFatura(compraParaExcluir);
      } else {
        toast.error(errorMsg);
      }
    }
  };

  // Fechar modal ao pressionar ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showModal) {
        handleCloseModal();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTransacao(null);
    resetForm();
  };

  const loadCategorias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financeiro/categorias`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategorias(response.data.categorias || []);
    } catch (error) {
      console.error('Error loading categorias:', error);
    }
  };

  const loadContas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financeiro/contas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContas(response.data.contas || []);
    } catch (error) {
      console.error('Error loading contas:', error);
    }
  };

  const loadTransacoes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Carregar receitas e despesas
      const [receitasRes, despesasRes] = await Promise.all([
        axios.get(`${API}/financeiro/receitas`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { mes: filtroMes, ano: filtroAno }
        }),
        axios.get(`${API}/financeiro/despesas`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { mes: filtroMes, ano: filtroAno }
        })
      ]);

      // Combinar e ordenar por data
      const todasTransacoes = [
        ...receitasRes.data.receitas.map(r => ({ ...r, tipo: 'receita' })),
        ...despesasRes.data.despesas.map(d => ({ ...d, tipo: 'despesa' }))
      ].sort((a, b) => new Date(b.data) - new Date(a.data));

      // Filtrar por tipo se selecionado
      let filtradas = filtroTipo 
        ? todasTransacoes.filter(t => t.tipo === filtroTipo)
        : todasTransacoes;
      
      // Filtrar por conta se selecionada
      if (filtroContaId) {
        filtradas = filtradas.filter(t => t.conta_id === filtroContaId);
      }

      setTransacoes(filtradas);
    } catch (error) {
      console.error('Error loading transações:', error);
      toast.error('Erro ao carregar transações');
    } finally {
      setLoading(false);
    }
  };

  const loadResumo = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Buscar dados do dashboard (que já tem saldo acumulado correto)
      const dashboardRes = await axios.get(`${API}/financeiro/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { mes: filtroMes, ano: filtroAno }
      });
      
      const dashData = dashboardRes.data.cards_principais;
      
      setResumo({
        saldo_atual: dashData.saldo_acumulado, // Saldo acumulado (inteligente)
        total_receitas: dashData.receitas, // Receitas pagas do mês
        total_despesas: dashData.despesas, // Despesas pagas do mês
        balanco_mensal: dashData.receitas - dashData.despesas, // Balanço do mês
        faturas_abertas: dashData.cartoes_credito // Faturas de cartão em aberto
      });
    } catch (error) {
      console.error('Error loading resumo:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const valorNumerico = parseCurrencyToFloat(formData.valor);
    
    if (!valorNumerico || valorNumerico <= 0) {
      toast.error('Valor deve ser maior que zero');
      return;
    }

    if (!formData.conta_id) {
      toast.error('Selecione uma conta');
      return;
    }

    if (!formData.categoria_id) {
      toast.error('Selecione uma categoria');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = tipoTransacao === 'receita' ? 'receitas' : 'despesas';
      
      // Verificar se é cartão de crédito
      const contaSelecionada = contas.find(c => c.id === formData.conta_id);
      const isCartaoCredito = contaSelecionada && contaSelecionada.tipo === 'cartao_credito';
      
      const payload = {
        data: formData.data,
        descricao: formData.descricao || null,
        valor: valorNumerico,
        categoria_id: formData.categoria_id,
        conta_id: formData.conta_id,
        [tipoTransacao === 'receita' ? 'recebido' : 'pago']: isCartaoCredito ? false : formData.pago_recebido
      };

      // Adicionar dados de parcelamento para despesas
      if (tipoTransacao === 'despesa' && formData.parcelado) {
        payload.parcelado = true;
        payload.numero_parcelas = parseInt(formData.numero_parcelas);
      }

      if (editingTransacao) {
        await axios.put(`${API}/financeiro/${endpoint}/${editingTransacao.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(`${tipoTransacao === 'receita' ? 'Receita' : 'Despesa'} atualizada com sucesso!`);
      } else {
        // Se for CARTÃO DE CRÉDITO, usar endpoint específico de compras
        if (tipoTransacao === 'despesa' && isCartaoCredito) {
          const compraPayload = {
            cartao_id: formData.conta_id,
            categoria_id: formData.categoria_id,
            descricao: formData.descricao || 'Compra no cartão',
            valor_total: valorNumerico,
            numero_parcelas: formData.parcelado ? parseInt(formData.numero_parcelas) : 1,
            data_compra: formData.data
          };
          
          await axios.post(`${API}/cartoes/compras`, compraPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          // Despesa normal ou receita
          await axios.post(`${API}/financeiro/${endpoint}`, payload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
        toast.success(`${tipoTransacao === 'receita' ? 'Receita' : 'Despesa'} criada com sucesso!`);
      }

      setShowModal(false);
      setEditingTransacao(null);
      setTipoTransacao('');
      resetForm();
      loadTransacoes();
      loadResumo();
      
      // Se for despesa de cartão, recarregar fatura também
      const contaCadastrada = contas.find(c => c.id === formData.conta_id);
      if (tipoTransacao === 'despesa' && contaCadastrada?.tipo === 'cartao_credito') {
        loadFatura();
      }
    } catch (error) {
      console.error('Error saving transação:', error);
      toast.error('Erro ao salvar transação');
    }
  };

  const handleDelete = async (transacao) => {
    // Verificar se é transação de cartão de crédito
    const conta = contas.find(c => c.id === transacao.conta_id);
    const isCartaoCredito = conta && conta.tipo === 'cartao_credito';
    
    if (isCartaoCredito) {
      // Transação de cartão - abrir modal inteligente
      setCompraParaExcluir(transacao);
      setShowDeleteCartaoModal(true);
      return;
    }
    
    // Transação normal - abrir modal de confirmação
    setTransacaoParaExcluir(transacao);
    setShowDeleteModal(true);
  };

  // Confirmar exclusão de transação normal
  const confirmarExclusaoTransacao = async () => {
    if (!transacaoParaExcluir) return;

    try {
      const token = localStorage.getItem('token');
      const endpoint = transacaoParaExcluir.tipo === 'receita' ? 'receitas' : 'despesas';
      
      await axios.delete(`${API}/financeiro/${endpoint}/${transacaoParaExcluir.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Transação excluída com sucesso!');
      
      // Fechar modal
      setShowDeleteModal(false);
      setTransacaoParaExcluir(null);
      
      // Recarregar dados
      loadTransacoes();
      loadResumo();
    } catch (error) {
      console.error('Error deleting transação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao excluir transação');
    }
  };

  const handleToggleStatus = async (transacao) => {
    const isReceita = transacao.tipo === 'receita';
    const currentStatus = isReceita ? transacao.recebido : transacao.pago;
    const newStatus = !currentStatus;
    const statusText = isReceita ? (newStatus ? 'Recebido' : 'Não recebido') : (newStatus ? 'Pago' : 'Não pago');

    try {
      const token = localStorage.getItem('token');
      const endpoint = transacao.tipo === 'receita' ? 'receitas' : 'despesas';
      
      const payload = {
        data: transacao.data,
        descricao: transacao.descricao,
        categoria_id: transacao.categoria_id,
        conta_id: transacao.conta_id,
        valor: transacao.valor,
        [isReceita ? 'recebido' : 'pago']: newStatus
      };

      await axios.put(`${API}/financeiro/${endpoint}/${transacao.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Marcado como ${statusText}!`);
      loadTransacoes();
      loadResumo();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const openEditModal = (transacao) => {
    setEditingTransacao(transacao);
    setTipoTransacao(transacao.tipo);
    
    // Formatar valor existente para moeda brasileira
    const valorFormatado = transacao.valor.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    
    setFormData({
      data: transacao.data,
      descricao: transacao.descricao || '',
      categoria_id: transacao.categoria_id || '',
      conta_id: transacao.conta_id || '',
      valor: valorFormatado,
      pago_recebido: transacao.tipo === 'receita' ? transacao.recebido : transacao.pago
    });
    setShowModal(true);
  };

  const openNewModal = (tipo) => {
    setTipoTransacao(tipo);
    setEditingTransacao(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      data: new Date().toISOString().split('T')[0],
      descricao: '',
      categoria_id: '',
      conta_id: '',
      valor: '',
      pago_recebido: true,
      parcelado: false,
      numero_parcelas: 2
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  const getCategoriaById = (categoriaId) => {
    return categorias.find(cat => cat.id === categoriaId);
  };

  const getContaById = (contaId) => {
    return contas.find(conta => conta.id === contaId);
  };

  const getCategoriaInfo = (categoriaValue) => {
    return CATEGORIAS_DESPESAS.find(c => c.value === categoriaValue) || CATEGORIAS_DESPESAS[7];
  };

  // Função para formatar valor como moeda brasileira (input)
  const formatInputCurrency = (value) => {
    // Remove tudo que não é dígito
    const digits = value.replace(/\D/g, '');
    
    if (!digits) return '';
    
    // Converte para número e divide por 100 (centavos)
    const number = parseInt(digits) / 100;
    
    // Formata com separadores brasileiros
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Função para converter valor formatado de volta para float
  const parseCurrencyToFloat = (value) => {
    if (!value) return 0;
    
    // Remove R$, espaços, pontos (separador de milhar)
    // Troca vírgula (separador decimal) por ponto
    const cleanValue = value
      .replace(/R\$/g, '')
      .replace(/\s/g, '')
      .replace(/\./g, '')
      .replace(/,/g, '.');
    
    return parseFloat(cleanValue) || 0;
  };

  // Handler para o campo de valor
  const handleValorChange = (e) => {
    const inputValue = e.target.value;
    const formatted = formatInputCurrency(inputValue);
    setFormData({ ...formData, valor: formatted });
  };

  return (
    <div style={{ display: 'flex', gap: '24px' }}>
      {/* Main Content - Transações */}
      <div style={{ flex: 1 }}>
        {/* Filters and Add Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          {/* Filters - Month Navigation + Type Filter */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {/* Month/Year Selector (Mobills style) - Agora com modal */}
            <div style={{ position: 'relative' }}>
              <button
                id="date-filter-button"
                onClick={() => {
                  setTempMes(filtroMes);
                  setTempAno(filtroAno);
                  setShowDatePicker(!showDatePicker);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 20px',
                  borderRadius: '12px',
                  background: isDarkMode 
                    ? 'linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(59, 130, 246, 0.15))'
                    : 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(59, 130, 246, 0.1))',
                  border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)'}`,
                  boxShadow: isDarkMode 
                    ? '0 4px 12px rgba(6, 182, 212, 0.15)' 
                    : '0 2px 8px rgba(6, 182, 212, 0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  minWidth: '180px',
                  justifyContent: 'center'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeColors.accent;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isDarkMode ? 'rgba(6, 182, 212, 0.3)' : 'rgba(6, 182, 212, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <span>
                  {['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 
                    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'][filtroMes - 1]}
                </span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Date Picker Modal - Estilo Mobills */}
              {showDatePicker && (
                <div
                  id="date-picker-modal"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    background: isDarkMode ? '#252b3b' : 'white',
                    border: `1px solid ${themeColors.border}`,
                    borderRadius: '12px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    width: '280px',
                    overflow: 'hidden',
                    padding: '16px'
                  }}
                >
                  {/* Year Selector */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    marginBottom: '16px',
                    padding: '12px',
                    background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                    borderRadius: '10px'
                  }}>
                    <button
                      onClick={() => setTempAno(tempAno - 1)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <span style={{
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: '700',
                      minWidth: '70px',
                      textAlign: 'center'
                    }}>
                      {tempAno}
                    </span>
                    <button
                      onClick={() => setTempAno(tempAno + 1)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: 'white',
                        cursor: 'pointer',
                        padding: '2px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>

                  {/* Month Grid */}
                  <div className="cq-grid-4" style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: '6px',
                    marginBottom: '16px'
                  }}>
                    {['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'].map((mes, index) => (
                      <button
                        key={mes}
                        onClick={() => {
                          setTempMes(index + 1);
                          setFiltroMes(index + 1);
                          setFiltroAno(tempAno);
                          setShowDatePicker(false);
                        }}
                        style={{
                          padding: '8px 4px',
                          borderRadius: '6px',
                          border: 'none',
                          background: (filtroMes === index + 1 && filtroAno === tempAno)
                            ? 'linear-gradient(135deg, #06B6D4, #3B82F6)' 
                            : (isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)'),
                          color: (filtroMes === index + 1 && filtroAno === tempAno) ? 'white' : themeColors.text,
                          fontSize: '11px',
                          fontWeight: (filtroMes === index + 1 && filtroAno === tempAno) ? '700' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!(filtroMes === index + 1 && filtroAno === tempAno)) {
                            e.currentTarget.style.background = isDarkMode 
                              ? 'rgba(6, 182, 212, 0.2)' 
                              : 'rgba(6, 182, 212, 0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!(filtroMes === index + 1 && filtroAno === tempAno)) {
                            e.currentTarget.style.background = isDarkMode 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(0, 0, 0, 0.03)';
                          }
                        }}
                      >
                        {mes}
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'space-between'
                  }}>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${themeColors.border}`,
                        background: 'transparent',
                        color: themeColors.text,
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => {
                        const now = new Date();
                        setTempMes(now.getMonth() + 1);
                        setTempAno(now.getFullYear());
                        setFiltroMes(now.getMonth() + 1);
                        setFiltroAno(now.getFullYear());
                        setShowDatePicker(false);
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        boxShadow: '0 2px 8px rgba(6, 182, 212, 0.3)'
                      }}
                    >
                      Atual
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Type Filter */}
            <div style={{ position: 'relative' }}>
              <button
                id="filter-type-button"
                onClick={() => {
                  const menu = document.getElementById('filter-menu');
                  menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '12px',
                  border: `1px solid ${themeColors.border}`,
                  background: isDarkMode ? '#252b3b' : themeColors.bgCard,
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = themeColors.accent;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)'}`;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = themeColors.border;
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {/* Bolinha colorida baseada no filtro */}
                <div style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  background: filtroTipo === '' ? '#8B5CF6' : 
                             (filtroTipo === 'receita' ? '#10B981' : 
                             (filtroTipo === 'despesa' ? '#EF4444' : '#3B82F6'))
                }} />
                {filtroTipo === '' && 'Transações'}
                {filtroTipo === 'despesa' && 'Despesas'}
                {filtroTipo === 'receita' && 'Receitas'}
                {filtroTipo === 'transferencia' && 'Transferências'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px' }}>
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Filter Dropdown Menu */}
              <div
                id="filter-menu"
                style={{
                  display: 'none',
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '8px',
                  background: isDarkMode ? '#252b3b' : 'white',
                  border: `1px solid ${themeColors.border}`,
                  borderRadius: '12px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '240px',
                  overflow: 'hidden'
                }}
              >
                <button
                  onClick={() => {
                    setFiltroTipo('');
                    document.getElementById('filter-menu').style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: filtroTipo === '' 
                      ? (isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)')
                      : 'transparent',
                    border: 'none',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode 
                      ? 'rgba(139, 92, 246, 0.1)' 
                      : 'rgba(139, 92, 246, 0.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = filtroTipo === '' 
                      ? (isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)')
                      : 'transparent';
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#8B5CF6',
                    flexShrink: 0
                  }} />
                  <span>Transações</span>
                </button>
                
                <button
                  onClick={() => {
                    setFiltroTipo('despesa');
                    document.getElementById('filter-menu').style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: filtroTipo === 'despesa' 
                      ? 'rgba(239, 68, 68, 0.1)'
                      : 'transparent',
                    border: 'none',
                    borderTop: `1px solid ${themeColors.border}`,
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = filtroTipo === 'despesa' 
                      ? 'rgba(239, 68, 68, 0.1)'
                      : 'transparent';
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#EF4444',
                    flexShrink: 0
                  }} />
                  <span>Despesas</span>
                </button>
                
                <button
                  onClick={() => {
                    setFiltroTipo('receita');
                    document.getElementById('filter-menu').style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: filtroTipo === 'receita' 
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'transparent',
                    border: 'none',
                    borderTop: `1px solid ${themeColors.border}`,
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = filtroTipo === 'receita' 
                      ? 'rgba(16, 185, 129, 0.1)'
                      : 'transparent';
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#10B981',
                    flexShrink: 0
                  }} />
                  <span>Receitas</span>
                </button>
                
                <button
                  onClick={() => {
                    setFiltroTipo('transferencia');
                    document.getElementById('filter-menu').style.display = 'none';
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: filtroTipo === 'transferencia' 
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'transparent',
                    border: 'none',
                    borderTop: `1px solid ${themeColors.border}`,
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '500',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = filtroTipo === 'transferencia' 
                      ? 'rgba(59, 130, 246, 0.1)'
                      : 'transparent';
                  }}
                >
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: '#3B82F6',
                    flexShrink: 0
                  }} />
                  <span>Transferências</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filtro por Conta */}
          <div style={{ position: 'relative' }}>
            <select
              value={filtroContaId}
              onChange={(e) => setFiltroContaId(e.target.value)}
              style={{
                padding: '10px 20px',
                borderRadius: '12px',
                border: `1px solid ${themeColors.border}`,
                background: isDarkMode ? '#252b3b' : themeColors.bgCard,
                color: themeColors.text,
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                outline: 'none',
                transition: 'all 0.3s ease',
                minWidth: '200px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = themeColors.accent;
                e.currentTarget.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)'}`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = themeColors.border;
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <option value="">Todas as Contas</option>
              {contas.map(conta => (
                <option key={conta.id} value={conta.id}>
                  {conta.nome}
                </option>
              ))}
            </select>
          </div>

          {/* Add Button with Dropdown */}
          <div style={{ position: 'relative' }}>
            <button
              id="nova-transacao-button"
              onClick={() => {
                const menu = document.getElementById('dropdown-menu');
                menu.style.display = menu.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                padding: '10px 24px',
                background: 'linear-gradient(135deg, #06B6D4, #3B82F6)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(6, 182, 212, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(6, 182, 212, 0.3)';
              }}
            >
              <Plus size={16} />
              Nova Transação
            </button>
            
            {/* Dropdown Menu */}
            <div
              id="dropdown-menu"
              style={{
                display: 'none',
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '8px',
                background: isDarkMode ? '#252b3b' : 'white',
                border: `1px solid ${themeColors.border}`,
                borderRadius: '12px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                zIndex: 1000,
                minWidth: '200px',
                overflow: 'hidden'
              }}
            >
              <button
                onClick={() => {
                  document.getElementById('dropdown-menu').style.display = 'none';
                  openNewModal('receita');
                }}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: 'transparent',
                  border: 'none',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                  e.currentTarget.style.color = '#10B981';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = themeColors.text;
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ArrowUpCircle size={16} style={{ color: '#10B981' }} />
                </div>
                <span>Receita</span>
              </button>
              
              <button
                onClick={() => {
                  document.getElementById('dropdown-menu').style.display = 'none';
                  openNewModal('despesa');
                }}
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  background: 'transparent',
                  border: 'none',
                  borderTop: `1px solid ${themeColors.border}`,
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  e.currentTarget.style.color = '#EF4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = themeColors.text;
                }}
              >
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <ArrowDownCircle size={16} style={{ color: '#EF4444' }} />
                </div>
                <span>Despesa</span>
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px' }}>
            <p style={{ color: themeColors.textSecondary }}>Carregando...</p>
          </div>
        ) : transacoes.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px',
            background: isDarkMode ? '#252b3b' : themeColors.bgCard,
            border: `1px solid ${themeColors.border}`,
            borderRadius: '12px'
          }}>
            <Wallet size={40} style={{ color: themeColors.textSecondary, marginBottom: '16px' }} />
            <p style={{ fontSize: '14px', color: themeColors.textSecondary, marginBottom: '16px' }}>
              Nenhuma transação encontrada para este período
            </p>
          </div>
        ) : (
          <div style={{
            background: isDarkMode ? '#252b3b' : themeColors.bgCard,
            border: `1px solid ${themeColors.border}`,
            borderRadius: '12px',
            overflow: 'hidden'
          }}>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Tipo</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Data</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Descrição</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Categoria</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Conta</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Valor</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Status</th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: themeColors.textSecondary }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {transacoes.map((transacao) => {
                  const isReceita = transacao.tipo === 'receita';
                  const categoria = getCategoriaById(transacao.categoria_id);
                  const conta = getContaById(transacao.conta_id);
                  const status = isReceita ? transacao.recebido : transacao.pago;
                  const isCartaoCredito = conta?.tipo === 'cartao_credito';
                  
                  return (
                    <tr
                      key={`${transacao.tipo}-${transacao.id}`}
                      style={{
                        borderTop: `1px solid ${themeColors.border}`
                      }}
                    >
                      <td style={{ padding: '16px' }}>
                        {isReceita ? (
                          <ArrowUpCircle size={16} style={{ color: '#10B981' }} />
                        ) : (
                          <ArrowDownCircle size={16} style={{ color: '#EF4444' }} />
                        )}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: themeColors.text }}>
                        {formatDate(transacao.data)}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: themeColors.text }}>
                        {transacao.descricao || '-'}
                      </td>
                      <td style={{ padding: '16px' }}>
                        {categoria ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '3px',
                              background: categoria.cor || '#3B82F6',
                              flexShrink: 0
                            }} />
                            <span style={{ 
                              fontSize: '14px', 
                              color: themeColors.text,
                              fontWeight: '500'
                            }}>
                              {categoria.nome}
                            </span>
                          </div>
                        ) : (
                          <span style={{ fontSize: '14px', color: themeColors.textSecondary }}>
                            -
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', color: themeColors.text }}>
                        {conta ? conta.nome : '-'}
                      </td>
                      <td style={{ padding: '16px', fontSize: '14px', fontWeight: '600', color: isReceita ? '#10B981' : '#EF4444', textAlign: 'right' }}>
                        {isReceita ? '+' : '-'} {formatCurrency(transacao.valor)}
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: status ? (isReceita ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)') : 'rgba(245, 158, 11, 0.1)',
                          color: status ? (isReceita ? '#10B981' : '#EF4444') : '#F59E0B'
                        }}>
                          {status ? (isReceita ? 'Recebido' : 'Pago') : 'Pendente'}
                        </span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          {/* Botão Inteligente - Marcar como Recebido/Pago (não aparece para cartão de crédito) */}
                          {!status && !isCartaoCredito && (
                            <button
                              onClick={() => handleToggleStatus(transacao)}
                              style={{
                                padding: '6px',
                                background: isReceita ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                border: `1px solid ${isReceita ? '#10B981' : '#EF4444'}`,
                                borderRadius: '6px',
                                cursor: 'pointer',
                                color: isReceita ? '#10B981' : '#EF4444',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease',
                                width: '32px',
                                height: '32px'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = isReceita ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = isReceita ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                              title="Efetivar"
                            >
                              ✓
                            </button>
                          )}
                          
                          <button
                            onClick={() => openEditModal(transacao)}
                            style={{
                              padding: '6px',
                              background: 'transparent',
                              border: `1px solid ${themeColors.border}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              color: themeColors.text
                            }}
                            title="Editar transação"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(transacao)}
                            style={{
                              padding: '6px',
                              background: 'transparent',
                              border: '1px solid rgba(239, 68, 68, 0.3)',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              color: '#EF4444'
                            }}
                            title="Excluir transação"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Sidebar - Resumo */}
      <div style={{ width: '280px', flexShrink: 0 }}>
        <div style={{ position: 'sticky', top: '96px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Saldo Atual */}
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            background: isDarkMode ? '#252b3b' : themeColors.bgCard,
            border: `1px solid ${themeColors.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Wallet size={16} style={{ color: themeColors.accent }} />
              <span style={{ fontSize: '12px', color: themeColors.textSecondary, fontWeight: '500' }}>
                Saldo Acumulado
              </span>
            </div>
            <p style={{ fontSize: '20px', fontWeight: '700', color: themeColors.text, margin: 0 }}>
              {formatCurrency(resumo.saldo_atual)}
            </p>
          </div>

          {/* Receitas */}
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            background: isDarkMode ? '#252b3b' : themeColors.bgCard,
            border: `1px solid ${themeColors.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <TrendingUp size={16} style={{ color: '#10B981' }} />
              <span style={{ fontSize: '12px', color: themeColors.textSecondary, fontWeight: '500' }}>
                Receitas
              </span>
            </div>
            <p style={{ fontSize: '20px', fontWeight: '700', color: '#10B981', margin: 0 }}>
              {formatCurrency(resumo.total_receitas)}
            </p>
          </div>

          {/* Despesas */}
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            background: isDarkMode ? '#252b3b' : themeColors.bgCard,
            border: `1px solid ${themeColors.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <TrendingDown size={16} style={{ color: '#EF4444' }} />
              <span style={{ fontSize: '12px', color: themeColors.textSecondary, fontWeight: '500' }}>
                Despesas
              </span>
            </div>
            <p style={{ fontSize: '20px', fontWeight: '700', color: '#EF4444', margin: 0 }}>
              {formatCurrency(resumo.total_despesas)}
            </p>
          </div>

          {/* Balanço Mensal */}
          <div style={{
            padding: '16px',
            borderRadius: '12px',
            background: isDarkMode ? '#252b3b' : themeColors.bgCard,
            border: `1px solid ${themeColors.border}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
              <Scale size={16} style={{ color: themeColors.accent }} />
              <span style={{ fontSize: '12px', color: themeColors.textSecondary, fontWeight: '500' }}>
                Balanço Mensal
              </span>
            </div>
            <p style={{ 
              fontSize: '20px', 
              fontWeight: '700', 
              color: resumo.balanco_mensal >= 0 ? '#10B981' : '#EF4444', 
              margin: 0 
            }}>
              {formatCurrency(resumo.balanco_mensal)}
            </p>
          </div>

          {/* Fatura do Cartão */}
          {contas.filter(c => c.tipo === 'cartao_credito').length > 0 && (
            <div style={{
              padding: '16px',
              borderRadius: '12px',
              background: isDarkMode ? '#252b3b' : themeColors.bgCard,
              border: `1px solid ${themeColors.border}`
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CreditCard size={16} style={{ color: '#8B5CF6' }} />
                  <span style={{ fontSize: '12px', color: themeColors.textSecondary, fontWeight: '500' }}>
                    Fatura do Cartão
                  </span>
                </div>
                {fatura && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '4px 8px',
                    borderRadius: '6px',
                    background: fatura.status === 'aberta' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: fatura.status === 'aberta' ? '#10B981' : '#EF4444',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    {fatura.status === 'aberta' ? (
                      <>
                        <CheckCircle size={10} />
                        ABERTA
                      </>
                    ) : (
                      <>
                        <XCircle size={10} />
                        FECHADA
                      </>
                    )}
                  </span>
                )}
              </div>

              {/* Seletor de Cartão */}
              {contas.filter(c => c.tipo === 'cartao_credito').length > 1 && (
                <select
                  value={cartaoSelecionado || ''}
                  onChange={(e) => setCartaoSelecionado(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: `1px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '12px',
                    marginBottom: '12px',
                    cursor: 'pointer'
                  }}
                >
                  {contas.filter(c => c.tipo === 'cartao_credito').map(cartao => (
                    <option key={cartao.id} value={cartao.id}>{cartao.nome}</option>
                  ))}
                </select>
              )}

              {loadingFatura ? (
                <p style={{ fontSize: '12px', color: themeColors.textSecondary, margin: '12px 0' }}>
                  Carregando...
                </p>
              ) : fatura ? (
                <>
                  <p style={{ fontSize: '20px', fontWeight: '700', color: '#8B5CF6', margin: '0 0 8px 0' }}>
                    {formatCurrency(fatura.total)}
                  </p>
                  
                  {/* Barra de Progresso do Limite */}
                  {(() => {
                    const cartao = contas.find(c => c.id === cartaoSelecionado);
                    const limite = cartao?.limite_credito || 0;
                    const percentual = limite > 0 ? (fatura.total / limite) * 100 : 0;
                    
                    return limite > 0 ? (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '10px', color: themeColors.textSecondary }}>
                            Limite usado
                          </span>
                          <span style={{ fontSize: '10px', fontWeight: '600', color: themeColors.text }}>
                            {percentual.toFixed(1)}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '6px',
                          borderRadius: '3px',
                          background: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${Math.min(percentual, 100)}%`,
                            height: '100%',
                            background: percentual > 80 ? '#EF4444' : percentual > 50 ? '#F59E0B' : '#10B981',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        <p style={{ fontSize: '10px', color: themeColors.textSecondary, margin: '4px 0 0 0' }}>
                          Disponível: {formatCurrency(fatura.limite_disponivel)}
                        </p>
                      </div>
                    ) : null;
                  })()}
                  
                  {/* Botão Pagar Fatura - Aparece sempre que houver parcelas pendentes (total > 0) */}
                  {fatura.total > 0 && (
                    <button
                      onClick={handleLiquidarFatura}
                      style={{
                        width: '100%',
                        marginTop: '12px',
                        padding: '10px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #10B981, #059669)',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                      }}
                    >
                      <CheckCircle size={16} />
                      💳 Pagar Fatura
                    </button>
                  )}
                </>
              ) : (
                <p style={{ fontSize: '12px', color: themeColors.textSecondary, margin: '12px 0' }}>
                  Sem despesas neste período
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div 
          onMouseDown={(e) => {
            // Se clique começou no backdrop, marcar para fechar
            if (e.target === e.currentTarget) {
              e.currentTarget.dataset.closeOnMouseUp = 'true';
            }
          }}
          onMouseUp={(e) => {
            // Só fechar se mousedown E mouseup foram no backdrop
            if (e.target === e.currentTarget && e.currentTarget.dataset.closeOnMouseUp === 'true') {
              handleCloseModal();
            }
            delete e.currentTarget.dataset.closeOnMouseUp;
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px'
          }}
        >
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              background: isDarkMode ? '#252b3b' : 'white',
              borderRadius: '16px',
              padding: '24px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            <h2 style={{
              fontSize: '18px',
              fontWeight: '700',
              color: themeColors.text,
              marginBottom: '20px'
            }}>
              {editingTransacao 
                ? `Editar ${tipoTransacao === 'receita' ? 'Receita' : 'Despesa'}`
                : `Nova ${tipoTransacao === 'receita' ? 'Receita' : 'Despesa'}`
              }
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Valor */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: themeColors.text, 
                  display: 'block', 
                  marginBottom: '6px' 
                }}>
                  Valor *
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute',
                    left: '16px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: themeColors.accent,
                    fontSize: '14px',
                    fontWeight: '600',
                    pointerEvents: 'none'
                  }}>
                    R$
                  </span>
                  <input
                    type="text"
                    value={formData.valor}
                    onChange={handleValorChange}
                    placeholder="0,00"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px 12px 48px',
                      borderRadius: '10px',
                      border: `2px solid ${themeColors.border}`,
                      background: isDarkMode ? '#1a1f2e' : 'white',
                      color: themeColors.text,
                      fontSize: '16px',
                      fontWeight: '600',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      letterSpacing: '0.5px'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = themeColors.accent;
                      e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = themeColors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
                <p style={{ 
                  fontSize: '12px', 
                  color: themeColors.textSecondary, 
                  marginTop: '6px',
                  fontStyle: 'italic'
                }}>
                  Digite apenas números. Ex: 1234,56
                </p>
              </div>

              {/* Foi Pago/Recebido - Não mostrar para cartão de crédito em despesas */}
              {!(tipoTransacao === 'despesa' && (() => {
                const contaSelecionada = contas.find(c => c.id === formData.conta_id);
                return contaSelecionada && contaSelecionada.tipo === 'cartao_credito';
              })()) && (
                <div style={{ 
                  marginBottom: '20px', 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  padding: '14px 16px',
                  background: isDarkMode ? 'rgba(6, 182, 212, 0.05)' : 'rgba(6, 182, 212, 0.03)',
                  borderRadius: '10px',
                  border: `1px solid ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`
                }}>
                  <input
                    type="checkbox"
                    id="pago-recebido-checkbox"
                    checked={formData.pago_recebido}
                    onChange={(e) => setFormData({ ...formData, pago_recebido: e.target.checked })}
                    style={{ 
                      width: '20px', 
                      height: '20px', 
                      cursor: 'pointer',
                      accentColor: themeColors.accent
                    }}
                  />
                  <label 
                    htmlFor="pago-recebido-checkbox"
                    style={{ 
                      fontSize: '14px', 
                      fontWeight: '500', 
                      color: themeColors.text, 
                      cursor: 'pointer',
                      userSelect: 'none'
                    }}
                  >
                    Foi {tipoTransacao === 'receita' ? 'recebido' : 'pago'}
                  </label>
                </div>
              )}

              {/* Data */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: themeColors.text, 
                  display: 'block', 
                  marginBottom: '6px' 
                }}>
                  Data *
                </label>
                <div style={{ position: 'relative' }}>
                  <Calendar 
                    size={18} 
                    style={{
                      position: 'absolute',
                      left: '16px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: themeColors.accent,
                      pointerEvents: 'none',
                      zIndex: 1
                    }}
                  />
                  <input
                    type="date"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px 12px 48px',
                      borderRadius: '10px',
                      border: `2px solid ${themeColors.border}`,
                      background: isDarkMode ? '#1a1f2e' : 'white',
                      color: themeColors.text,
                      fontSize: '14px',
                      fontWeight: '500',
                      outline: 'none',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = themeColors.accent;
                      e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = themeColors.border;
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                </div>
              </div>

              {/* Categoria - Para ambos receitas e despesas */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: themeColors.text, 
                  display: 'block', 
                  marginBottom: '6px' 
                }}>
                  Categoria *
                </label>
                <select
                  value={formData.categoria_id}
                  onChange={(e) => setFormData({ ...formData, categoria_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '14px',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Selecione uma categoria</option>
                  {categorias
                    .filter(cat => cat.tipo === tipoTransacao)
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nome}</option>
                    ))}
                  {categorias.filter(cat => cat.tipo === tipoTransacao).length === 0 && (
                    <option value="" disabled>Nenhuma categoria de {tipoTransacao} cadastrada</option>
                  )}
                </select>
              </div>

              {/* Conta */}
              <div style={{ marginBottom: '12px' }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: themeColors.text, 
                  display: 'block', 
                  marginBottom: '6px' 
                }}>
                  Conta *
                </label>
                <select
                  value={formData.conta_id}
                  onChange={(e) => setFormData({ ...formData, conta_id: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '10px',
                    border: `2px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '14px',
                    cursor: 'pointer',
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <option value="">Selecione uma conta</option>
                  {contas.map(conta => (
                    <option key={conta.id} value={conta.id}>{conta.nome}</option>
                  ))}
                  {contas.length === 0 && (
                    <option value="" disabled>Nenhuma conta cadastrada</option>
                  )}
                </select>
              </div>

              {/* Parcelamento - Apenas para despesas em cartão de crédito */}
              {tipoTransacao === 'despesa' && (() => {
                const contaSelecionada = contas.find(c => c.id === formData.conta_id);
                return contaSelecionada && contaSelecionada.tipo === 'cartao_credito';
              })() && (
                <>
                  {/* Checkbox Parcelado */}
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      cursor: 'pointer',
                      padding: '10px',
                      borderRadius: '10px',
                      background: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
                      border: `2px solid ${formData.parcelado ? '#8B5CF6' : 'transparent'}`,
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={formData.parcelado}
                        onChange={(e) => setFormData({ ...formData, parcelado: e.target.checked })}
                        style={{
                          width: '18px',
                          height: '18px',
                          cursor: 'pointer',
                          accentColor: '#8B5CF6'
                        }}
                      />
                      <CreditCard size={16} style={{ color: '#8B5CF6' }} />
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: themeColors.text
                      }}>
                        Compra Parcelada
                      </span>
                    </label>
                  </div>

                  {/* Número de Parcelas */}
                  {formData.parcelado && (
                    <>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: themeColors.text,
                          display: 'block',
                          marginBottom: '6px'
                        }}>
                          Número de Parcelas *
                        </label>
                        <input
                          type="number"
                          min="2"
                          max="48"
                          value={formData.numero_parcelas}
                          onChange={(e) => setFormData({ ...formData, numero_parcelas: parseInt(e.target.value) || 2 })}
                          required
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            borderRadius: '10px',
                            border: `2px solid ${themeColors.border}`,
                            background: isDarkMode ? '#1a1f2e' : 'white',
                            color: themeColors.text,
                            fontSize: '14px',
                            fontWeight: '500',
                            outline: 'none'
                          }}
                        />
                      </div>

                      {/* Valor por Parcela */}
                      <div style={{
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: isDarkMode ? 'rgba(16, 185, 129, 0.1)' : 'rgba(16, 185, 129, 0.05)',
                        border: `1px solid ${isDarkMode ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.2)'}`,
                        marginBottom: '12px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: themeColors.textSecondary }}>
                            Valor por parcela:
                          </span>
                          <span style={{ fontSize: '15px', fontWeight: '700', color: '#10B981' }}>
                            {formatCurrency(parseCurrencyToFloat(formData.valor) / (parseInt(formData.numero_parcelas) || 1))}
                          </span>
                        </div>
                        <p style={{ fontSize: '11px', color: themeColors.textSecondary, margin: '6px 0 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Calendar size={11} style={{ flexShrink: 0 }} />
                          Serão criadas {formData.numero_parcelas} despesas nos próximos meses
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Descrição */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  color: themeColors.text, 
                  display: 'block', 
                  marginBottom: '6px' 
                }}>
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder={tipoTransacao === 'receita' ? 'Ex: Faturamento AdSense Janeiro' : 'Ex: Mensalidade CapCut Pro, Chat GPT Plus'}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: `2px solid ${themeColors.border}`,
                    background: isDarkMode ? '#1a1f2e' : 'white',
                    color: themeColors.text,
                    fontSize: '14px',
                    resize: 'vertical',
                    outline: 'none',
                    transition: 'all 0.2s ease',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = themeColors.accent;
                    e.target.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.15)' : 'rgba(6, 182, 212, 0.1)'}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = themeColors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTransacao(null);
                    setTipoTransacao('');
                    resetForm();
                  }}
                  style={{
                    padding: '12px 24px',
                    background: 'transparent',
                    border: `2px solid ${themeColors.border}`,
                    borderRadius: '10px',
                    color: themeColors.text,
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 32px',
                    background: tipoTransacao === 'receita' 
                      ? 'linear-gradient(135deg, #10B981, #059669)' 
                      : 'linear-gradient(135deg, #EF4444, #DC2626)',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    boxShadow: tipoTransacao === 'receita'
                      ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                      : '0 4px 12px rgba(239, 68, 68, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = tipoTransacao === 'receita'
                      ? '0 6px 20px rgba(16, 185, 129, 0.4)'
                      : '0 6px 20px rgba(239, 68, 68, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = tipoTransacao === 'receita'
                      ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                      : '0 4px 12px rgba(239, 68, 68, 0.3)';
                  }}
                >
                  {editingTransacao ? 'Salvar Alterações' : 'Criar Transação'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Pagamento de Fatura */}
      {showLiquidarModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
            padding: '16px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLiquidarModal(false);
              setContaPagamentoSelecionada('');
            }
          }}
        >
          <div
            style={{
              background: isDarkMode ? '#1a1f2e' : 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: `1px solid ${themeColors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10B981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)'
              }}>
                <CreditCard size={32} color="white" />
              </div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                Pagar Fatura do Cartão
              </h2>
              <p style={{
                fontSize: '14px',
                color: themeColors.textSecondary
              }}>
                {contas.find(c => c.id === cartaoSelecionado)?.nome || 'Cartão'}
              </p>
            </div>

            {/* Valor da Fatura */}
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: isDarkMode ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)',
              border: `2px solid ${isDarkMode ? 'rgba(139, 92, 246, 0.3)' : 'rgba(139, 92, 246, 0.2)'}`,
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <p style={{
                fontSize: '14px',
                color: themeColors.textSecondary,
                marginBottom: '8px'
              }}>
                Valor Total da Fatura
              </p>
              <p style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#8B5CF6'
              }}>
                {formatCurrency(fatura?.total || 0)}
              </p>
              <p style={{
                fontSize: '12px',
                color: themeColors.textSecondary,
                marginTop: '8px'
              }}>
                Referente a {['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'][filtroMes - 1]}/{filtroAno}
              </p>
            </div>

            {/* Aviso de Antecipação (se aplicável) */}
            {(() => {
              const cartao = contas.find(c => c.id === cartaoSelecionado);
              const hoje = new Date();
              const diaVencimento = cartao?.dia_vencimento || 10;
              const mesVencimento = filtroMes;
              const anoVencimento = filtroAno;
              
              // Criar data de vencimento
              const dataVencimento = new Date(anoVencimento, mesVencimento - 1, diaVencimento);
              
              const isAntecipacao = hoje < dataVencimento;
              
              if (isAntecipacao) {
                const diasAntecipacao = Math.ceil((dataVencimento - hoje) / (1000 * 60 * 60 * 24));
                return (
                  <div style={{
                    padding: '16px',
                    borderRadius: '10px',
                    background: isDarkMode ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.1)',
                    border: `1px solid ${isDarkMode ? 'rgba(245, 158, 11, 0.4)' : 'rgba(245, 158, 11, 0.3)'}`,
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <AlertCircle size={20} color="#F59E0B" style={{ marginTop: '2px', flexShrink: 0 }} />
                    <div>
                      <p style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#F59E0B',
                        marginBottom: '4px'
                      }}>
                        ⚠️ Pagamento Antecipado
                      </p>
                      <p style={{
                        fontSize: '12px',
                        color: themeColors.textSecondary,
                        lineHeight: '1.5'
                      }}>
                        Você está pagando {diasAntecipacao} dia(s) antes do vencimento ({diaVencimento}/{String(mesVencimento).padStart(2, '0')}/{anoVencimento}).
                        O limite será liberado imediatamente.
                      </p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Seleção de Conta de Débito */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                Debitar de qual conta?
              </label>
              <select
                value={contaPagamentoSelecionada}
                onChange={(e) => setContaPagamentoSelecionada(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: `2px solid ${themeColors.border}`,
                  background: isDarkMode ? '#252b3b' : 'white',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = themeColors.accent;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)'}`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = themeColors.border;
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="">Selecione uma conta...</option>
                {contas.filter(c => c.tipo !== 'cartao_credito').map(conta => (
                  <option key={conta.id} value={conta.id}>
                    {conta.nome} ({conta.tipo === 'conta_corrente' ? 'Conta Corrente' : 
                                   conta.tipo === 'poupanca' ? 'Poupança' :
                                   conta.tipo === 'dinheiro' ? 'Dinheiro' : conta.tipo})
                  </option>
                ))}
              </select>
            </div>

            {/* Resumo do Que Será Feito */}
            <div style={{
              padding: '16px',
              borderRadius: '10px',
              background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
              marginBottom: '24px'
            }}>
              <p style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#3B82F6',
                marginBottom: '12px'
              }}>
                💡 O que acontecerá:
              </p>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '12px',
                color: themeColors.textSecondary,
                lineHeight: '1.8'
              }}>
                <li>✓ Parcelas do mês marcadas como <strong>pagas</strong></li>
                <li>✓ Limite do cartão <strong>liberado</strong> imediatamente</li>
                <li>✓ Registro de quitação salvo (histórico)</li>
                <li>⚠️ <strong>NÃO cria nova despesa</strong> (parcelas já contam)</li>
              </ul>
            </div>

            {/* Botões de Ação */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowLiquidarModal(false);
                  setContaPagamentoSelecionada('');
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: `2px solid ${themeColors.border}`,
                  background: 'transparent',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarPagamentoFatura}
                disabled={!contaPagamentoSelecionada}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: contaPagamentoSelecionada 
                    ? 'linear-gradient(135deg, #10B981, #059669)' 
                    : isDarkMode ? '#374151' : '#E5E7EB',
                  color: contaPagamentoSelecionada ? 'white' : isDarkMode ? '#6B7280' : '#9CA3AF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: contaPagamentoSelecionada ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  boxShadow: contaPagamentoSelecionada ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (contaPagamentoSelecionada) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.4)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = contaPagamentoSelecionada ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none';
                }}
              >
                ✓ Confirmar Pagamento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reabrir Fatura */}
      {showReabrirFaturaModal && compraParaReabrir && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
            padding: '16px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowReabrirFaturaModal(false);
              setCompraParaReabrir(null);
            }
          }}
        >
          <div
            style={{
              background: isDarkMode ? '#1a1f2e' : 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: `1px solid ${themeColors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(245, 158, 11, 0.3)'
              }}>
                <AlertCircle size={32} color="white" />
              </div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                Reabrir Fatura
              </h2>
              <p style={{
                fontSize: '14px',
                color: themeColors.textSecondary
              }}>
                {contas.find(c => c.id === compraParaReabrir.conta_id)?.nome || 'Cartão'}
              </p>
            </div>

            {/* Explicação */}
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: isDarkMode ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)',
              border: `2px solid ${isDarkMode ? 'rgba(245, 158, 11, 0.3)' : 'rgba(245, 158, 11, 0.2)'}`,
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Lightbulb size={16} style={{ color: '#F59E0B' }} />
                <span style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#F59E0B'
                }}>
                  Por que reabrir?
                </span>
              </div>
              <p style={{
                fontSize: '14px',
                color: themeColors.text,
                lineHeight: '1.6',
                marginBottom: '12px'
              }}>
                Esta compra possui parcelas que já foram pagas. Para excluí-la, é necessário primeiro reabrir a fatura do cartão.
              </p>
              <p style={{
                fontSize: '13px',
                color: themeColors.textSecondary,
                lineHeight: '1.5'
              }}>
                ⚠️ Ao reabrir, o pagamento da fatura será estornado e as parcelas voltarão a aparecer como pendentes.
              </p>
            </div>

            {/* Detalhes da Compra */}
            <div style={{
              padding: '16px',
              borderRadius: '10px',
              background: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              border: `1px solid ${themeColors.border}`,
              marginBottom: '24px'
            }}>
              <p style={{
                fontSize: '14px',
                fontWeight: '600',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                {compraParaReabrir.descricao}
              </p>
              <p style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#EF4444',
                marginBottom: '8px'
              }}>
                {formatCurrency(compraParaReabrir.valor)}
              </p>
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: themeColors.textSecondary }}>
                <span>📅 {formatDate(compraParaReabrir.data)}</span>
                {compraParaReabrir.numero_parcelas && (
                  <span>🔢 {compraParaReabrir.numero_parcelas}x</span>
                )}
              </div>
            </div>

            {/* Botões de Ação */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowReabrirFaturaModal(false);
                  setCompraParaReabrir(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: `2px solid ${themeColors.border}`,
                  background: 'transparent',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarReabrirFatura}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(245, 158, 11, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                }}
              >
                🔄 Reabrir Fatura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Transação Normal */}
      {showDeleteModal && transacaoParaExcluir && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
            padding: '16px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false);
              setTransacaoParaExcluir(null);
            }
          }}
        >
          <div
            style={{
              background: isDarkMode ? '#1a1f2e' : 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '100%',
              maxWidth: '450px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: `1px solid ${themeColors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)'
              }}>
                <Trash2 size={32} color="white" />
              </div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                Excluir Transação
              </h2>
              <p style={{
                fontSize: '14px',
                color: themeColors.textSecondary
              }}>
                Esta ação não pode ser desfeita
              </p>
            </div>

            {/* Detalhes da Transação */}
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
              border: `2px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
              marginBottom: '24px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                {transacaoParaExcluir.tipo === 'receita' ? (
                  <ArrowUpCircle size={16} style={{ color: '#10B981' }} />
                ) : (
                  <ArrowDownCircle size={16} style={{ color: '#EF4444' }} />
                )}
                <span style={{
                  fontSize: '12px',
                  fontWeight: '600',
                  color: transacaoParaExcluir.tipo === 'receita' ? '#10B981' : '#EF4444',
                  textTransform: 'uppercase'
                }}>
                  {transacaoParaExcluir.tipo}
                </span>
              </div>
              <p style={{
                fontSize: '16px',
                fontWeight: '600',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                {transacaoParaExcluir.descricao || 'Sem descrição'}
              </p>
              <p style={{
                fontSize: '20px',
                fontWeight: '700',
                color: transacaoParaExcluir.tipo === 'receita' ? '#10B981' : '#EF4444'
              }}>
                {transacaoParaExcluir.tipo === 'receita' ? '+' : '-'} {formatCurrency(transacaoParaExcluir.valor)}
              </p>
              <div style={{ display: 'flex', gap: '16px', marginTop: '12px' }}>
                <p style={{
                  fontSize: '12px',
                  color: themeColors.textSecondary
                }}>
                  📅 {formatDate(transacaoParaExcluir.data)}
                </p>
                {getCategoriaById(transacaoParaExcluir.categoria_id) && (
                  <p style={{
                    fontSize: '12px',
                    color: themeColors.textSecondary
                  }}>
                    📁 {getCategoriaById(transacaoParaExcluir.categoria_id).nome}
                  </p>
                )}
              </div>
            </div>

            {/* Botões de Ação */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setTransacaoParaExcluir(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: `2px solid ${themeColors.border}`,
                  background: 'transparent',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarExclusaoTransacao}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                }}
              >
                🗑️ Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Compra de Cartão */}
      {showDeleteCartaoModal && compraParaExcluir && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
            padding: '16px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteCartaoModal(false);
              setCompraParaExcluir(null);
            }
          }}
        >
          <div
            style={{
              background: isDarkMode ? '#1a1f2e' : 'white',
              borderRadius: '16px',
              padding: '32px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              border: `1px solid ${themeColors.border}`
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ marginBottom: '24px', textAlign: 'center' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)'
              }}>
                <Trash2 size={32} color="white" />
              </div>
              <h2 style={{
                fontSize: '24px',
                fontWeight: '700',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                Excluir Compra do Cartão
              </h2>
              <p style={{
                fontSize: '14px',
                color: themeColors.textSecondary
              }}>
                {contas.find(c => c.id === compraParaExcluir.conta_id)?.nome || 'Cartão'}
              </p>
            </div>

            {/* Detalhes da Compra */}
            <div style={{
              padding: '20px',
              borderRadius: '12px',
              background: isDarkMode ? 'rgba(239, 68, 68, 0.1)' : 'rgba(239, 68, 68, 0.05)',
              border: `2px solid ${isDarkMode ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.2)'}`,
              marginBottom: '24px'
            }}>
              <p style={{
                fontSize: '16px',
                fontWeight: '600',
                color: themeColors.text,
                marginBottom: '8px'
              }}>
                {compraParaExcluir.descricao}
              </p>
              <p style={{
                fontSize: '20px',
                fontWeight: '700',
                color: '#EF4444'
              }}>
                {formatCurrency(compraParaExcluir.valor)}
              </p>
              {compraParaExcluir.numero_parcelas && (
                <p style={{
                  fontSize: '12px',
                  color: themeColors.textSecondary,
                  marginTop: '8px'
                }}>
                  🔢 Parcela {compraParaExcluir.numero_parcela} de {compraParaExcluir.numero_parcelas}
                  {compraParaExcluir.numero_parcelas > 1 && (
                    <span style={{ fontWeight: '600', color: '#EF4444' }}>
                      {' '}→ Serão excluídas {compraParaExcluir.numero_parcelas} parcela(s)
                    </span>
                  )}
                </p>
              )}
              {compraParaExcluir.categoria_nome && (
                <p style={{
                  fontSize: '12px',
                  color: themeColors.textSecondary,
                  marginTop: '4px'
                }}>
                  📁 {compraParaExcluir.categoria_nome}
                </p>
              )}
            </div>

            {/* Opções de Exclusão */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: themeColors.text,
                marginBottom: '12px'
              }}>
                O que deseja fazer?
              </label>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  borderRadius: '8px',
                  border: `2px solid ${deleteAction === 'todas' ? themeColors.accent : themeColors.border}`,
                  background: deleteAction === 'todas' ? (isDarkMode ? 'rgba(6, 182, 212, 0.1)' : 'rgba(6, 182, 212, 0.05)') : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="radio"
                    value="todas"
                    checked={deleteAction === 'todas'}
                    onChange={(e) => setDeleteAction(e.target.value)}
                    style={{ marginRight: '12px', cursor: 'pointer' }}
                  />
                  <div>
                    <p style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: themeColors.text,
                      marginBottom: '4px'
                    }}>
                      🗑️ Excluir compra completa
                    </p>
                    <p style={{
                      fontSize: '12px',
                      color: themeColors.textSecondary
                    }}>
                      Remove todas as parcelas desta compra
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Aviso */}
            <div style={{
              padding: '16px',
              borderRadius: '10px',
              background: isDarkMode ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)',
              border: `1px solid ${isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.2)'}`,
              marginBottom: '24px'
            }}>
              <p style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#3B82F6',
                marginBottom: '12px'
              }}>
                ⚠️ Atenção:
              </p>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '12px',
                color: themeColors.textSecondary,
                lineHeight: '1.8'
              }}>
                <li>Esta ação não pode ser desfeita</li>
                <li>O limite do cartão será liberado</li>
                <li>Se houver parcelas pagas, você precisará reabrir a fatura primeiro</li>
              </ul>
            </div>

            {/* Botões de Ação */}
            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowDeleteCartaoModal(false);
                  setCompraParaExcluir(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: `2px solid ${themeColors.border}`,
                  background: 'transparent',
                  color: themeColors.text,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluirCompraCartao}
                style={{
                  flex: 1,
                  padding: '12px 24px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.3)';
                }}
              >
                🗑️ Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransacoesPage;
